import {
  AiActionKind,
  AiPromptStatusResponse,
  AiSourceReference,
  AiSourceUsage,
} from 'src/redux/aiNarrative/types';
import {
  AiPromptTemplate,
  AiTemplateAction,
} from 'src/redux/aiPromptTemplates/types';
import { AiQuota } from 'src/redux/aiQuota/types';

interface ApiResponse {
  status?: number;
  data?: unknown;
}

export function readApiResponse(
  response: ApiResponse,
  request: string,
): unknown {
  if (!response) {
    throw new Error(`${request}: no response received`);
  }

  const status = response.status ?? 0;
  if (status < 200 || status >= 300) {
    throw new Error(`${request}: request failed with status ${status}`);
  }

  return response.data;
}

const TEMPLATE_LIST_FIELD = 'prompt_templates';

export function asTemplateList(data: unknown): AiPromptTemplate[] {
  if (Array.isArray(data)) {
    return data;
  }
  if (!data || typeof data !== 'object') {
    return [];
  }

  const list = (data as Record<string, unknown>)[TEMPLATE_LIST_FIELD];
  if (Array.isArray(list)) {
    return list;
  }

  return [data as Record<string, unknown>];
}

const ACTION_FIELD = 'ai_action';

const ACTION_LABEL_FIELD = 'action_label';

const TEMPLATE_TEXT_FIELD = 'prompt';

const GENERATED_TEXT_FIELD = 'response';

const USED_DOCUMENTS_FIELD = 'used_documents';

const PROMPT_TYPE_FIELD = 'prompt_type';

// Celery task states: the payload carries a task_id and PENDING/SUCCESS, so the
// remaining states are its own.
const FAILED_STATUSES = ['failure', 'revoked'];

const PENDING_STATUSES = ['pending', 'received', 'started', 'retry'];

const SUCCEEDED_STATUSES = ['success'];

function readString(
  source: Record<string, unknown>,
  field: string,
): string | null {
  const value = source[field];
  return typeof value === 'string' && value.trim() ? value : null;
}

function readNumber(
  source: Record<string, unknown>,
  field: string,
): number | null {
  const value = source[field];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

const QUOTA_LIMIT_FIELD = 'ai_requests_limit';

const QUOTA_USED_FIELD = 'user_ai_requests';

export function readQuota(payload: unknown): AiQuota | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const record = payload as Record<string, unknown>;
  const limit = readNumber(record, QUOTA_LIMIT_FIELD);
  const used = readNumber(record, QUOTA_USED_FIELD);

  return limit === null || used === null ? null : { limit, used };
}

const QUOTA_EXCEEDED_DETAIL = /too many requests/i;

export function isQuotaExceededResponse(payload: unknown): boolean {
  const detail = (payload as Record<string, unknown>)?.error;
  return typeof detail === 'string' && QUOTA_EXCEEDED_DETAIL.test(detail);
}

export function isQuotaExceededError(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: unknown } })
    ?.response;

  if (!response) {
    return false;
  }
  // Recognised by the body, not the status: the backend has already moved from
  // 403 to 400, and an ordinary failure carries no such message.
  return response.status === 429 || isQuotaExceededResponse(response.data);
}

export function readTemplateActions(items: AiPromptTemplate[]): {
  primary: AiTemplateAction[];
  contextual: AiTemplateAction[];
} {
  const primary: AiTemplateAction[] = [];
  const contextual: AiTemplateAction[] = [];

  (items || []).forEach(item => {
    const action = readString(item, ACTION_FIELD);
    const label = readString(item, ACTION_LABEL_FIELD);
    const promptType = readString(item, PROMPT_TYPE_FIELD);
    if (!action || !label) {
      return;
    }
    if (promptType === 'primary') {
      primary.push({ action, label });
    } else if (promptType === 'contextual') {
      contextual.push({ action, label });
    }
  });

  return { primary, contextual };
}

export function findTemplateItem(
  items: AiPromptTemplate[],
  actionKind: AiActionKind,
): AiPromptTemplate | undefined {
  const list = items || [];
  return list.find(item => readString(item, ACTION_FIELD) === actionKind);
}

export function pickTemplate(
  items: AiPromptTemplate[],
  actionKind: AiActionKind,
): string {
  const match = findTemplateItem(items, actionKind);

  if (!match) {
    const list = items || [];
    const seen = list
      .map(
        item =>
          readString(item, ACTION_FIELD) || `{${Object.keys(item).join(',')}}`,
      )
      .join(', ');
    throw new Error(
      `No AI prompt template for "${actionKind}" (available: ${seen || 'none'})`,
    );
  }

  const text = readString(match, TEMPLATE_TEXT_FIELD);
  if (!text) {
    throw new Error(
      `AI prompt template "${actionKind}" carries no ${TEMPLATE_TEXT_FIELD} (fields: ${Object.keys(
        match,
      ).join(', ')})`,
    );
  }
  return text;
}

function statusIn(status: unknown, values: string[]): boolean {
  return (
    typeof status === 'string' && values.includes(status.trim().toLowerCase())
  );
}

export function isFailedStatus(status: unknown): boolean {
  return statusIn(status, FAILED_STATUSES);
}

export function isPendingStatus(status: unknown): boolean {
  return statusIn(status, PENDING_STATUSES);
}

export function isSucceededStatus(status: unknown): boolean {
  return statusIn(status, SUCCEEDED_STATUSES);
}

export function readGeneratedText(
  payload: AiPromptStatusResponse,
): string | null {
  return payload ? readString(payload, GENERATED_TEXT_FIELD) : null;
}

const PIPE_FIELDS = 5;
const PIPE_USAGE = 3;
const PIPE_DETAILS = 4;
const USAGE_LABEL = /^\s*USED FOR\s+([A-Z][A-Z\s]*?)\s*(?::\s*|$)/i;

function toUsage(value?: string): AiSourceUsage['usage'] {
  const label = (value || '').trim().toUpperCase().replace(/\s+/g, '_');
  if (label.startsWith('CONTENT')) {
    return 'content';
  }
  if (label.startsWith('STYLE')) {
    return 'style';
  }
  return label.startsWith('NOT_USED') ? 'not_used' : null;
}

export function readSourceUsage(
  details?: string,
  usage?: string,
): AiSourceUsage {
  const text = (details || '').trim();
  const declared = toUsage(usage);

  const fields = text.split('|');
  if (fields.length >= PIPE_FIELDS) {
    return {
      usage: declared ?? toUsage(fields[PIPE_USAGE]),
      text: fields.slice(PIPE_DETAILS).join('|').trim(),
    };
  }

  const match = text.match(USAGE_LABEL);
  if (!match) {
    return { usage: declared, text };
  }

  return {
    usage: declared ?? toUsage(match[1]),
    text: text.slice(match[0].length).trim(),
  };
}

export function readUsedDocuments(
  payload: AiPromptStatusResponse,
): AiSourceReference[] {
  const documents = payload?.[USED_DOCUMENTS_FIELD];
  if (!Array.isArray(documents)) {
    return [];
  }

  const references: AiSourceReference[] = [];
  documents.forEach((document, index) => {
    if (!document || typeof document !== 'object') {
      return;
    }
    const record = document as Record<string, unknown>;
    const filename = readString(record, 'filename');
    if (!filename) {
      return;
    }
    const location =
      readString(record, 'presigned_url') || readString(record, 'url');
    const sourceId = readString(record, 'source_id');
    const docType = readString(record, 'doc_type');
    const docTypeLabel = readString(record, 'full_doc_type');
    const { usage, text } = readSourceUsage(
      readString(record, 'details'),
      readString(record, 'usage'),
    );
    if (usage === 'not_used') {
      return;
    }

    references.push({
      id: `${filename}-${index}`,
      title: filename,
      filename,
      url: location && /^https?:\/\//i.test(location) ? location : undefined,
      source_id: sourceId || undefined,
      doc_type: docType || undefined,
      doc_type_label: docTypeLabel || undefined,
      details: text || undefined,
      usage,
    });
  });

  return references;
}
